import {
  AddressNullableSchema,
  AddressSchema,
} from 'lib/consumer/address/address.schema';
import {
  JOB_START_DATE,
  JOB_TYPE,
} from 'lib/sdks/jobs-management/job.constants';
import { z } from 'zod';
import { JOB_STATUSES } from './job.constants';

export enum FulfilmentType {
  ENQUIRY = 'ENQUIRY',
  BOOKING_ENQUIRY = 'BOOKING_ENQUIRY',
}

const JobRefinementFormSchema = z.object({
  formId: z.string(),
  experimentId: z.string().optional(),
  variant: z.string().optional(),
  experimentName: z.string().optional(),
  questions: z.array(
    z.object({
      title: z.string(),
      questionId: z.string(),
      answers: z.array(
        z.object({
          key: z.string().optional(),
          value: z.string(),
        }),
      ),
      format: z.string(),
    }),
  ),
});

const ServiceSchema = z.object({
  serviceId: z.string(),
  serviceVersionId: z.string(),
});

export const JobBaseSchema = z.object({
  categoryId: z.number(),
  description: z.string(),
  type: z.enum(['REQUEST_A_QUOTE', 'DIRECT_MESSAGE']),
  postcode: z.string().optional(),
  address: AddressSchema.optional(),
  trades: z.array(z.number()).min(1).max(3).optional(),
  preferredStart: z
    .object({
      id: z.string(),
      date: z.string().optional(),
    })
    .optional(),
  details: z
    .array(
      z.object({
        questionId: z.number(),
        question: z.string(),
        answerId: z.number(),
        answer: z.string(),
      }),
    )
    .optional(),
  mediaAttachmentIds: z.array(z.string()).optional(),
  jobRefinementForm: JobRefinementFormSchema.nullish(),
  creationSource: z.string().optional(),
  fulfilmentType: z.nativeEnum(FulfilmentType).optional(),
  service: ServiceSchema.optional(),
});

export const ContactedTradeSchema = z.object({
  id: z.number(),
  name: z.string(),
  logoUrl: z.string().optional(),
  address: AddressNullableSchema.optional(),
  reviews: z.object({
    score: z.number(),
    count: z.number(),
  }),
  tradeApp: z.boolean(),
  channelId: z.string().optional(),
  contact: z
    .object({
      label: z.string().optional(),
      number: z.string().optional(),
    })
    .optional(),
  opportunityId: z.string(),
  status: z.string(),
  uniqueName: z.string(),
});

export const JobSchema = JobBaseSchema.omit({
  categoryId: true,
  type: true,
}).extend({
  id: z.string(),
  // TODO find out all the different statuses
  status: z.enum(Object.keys(JOB_STATUSES) as [keyof typeof JOB_STATUSES]),
  category: z.object({
    id: z.number(),
    label: z.string(),
  }),
  createdAt: z.string(),
  trades: z.array(ContactedTradeSchema),
  activeCardCheckComplete: z.boolean(),
});

export const JobResponseSchema = JobSchema.extend({
  isNotHam: z.boolean().optional(),
});

export const JobResponsePublicSchema = JobSchema.extend({
  token: z.string().optional(),
  isNotHam: z.boolean().optional(),
});

export const JobsResponseSchema = z.object({
  data: z.array(JobSchema),
  page: z.number(),
  size: z.number(),
  total: z.number(),
});

export const JobRequestBaseSchema = JobBaseSchema.extend({
  type: z.nativeEnum(JOB_TYPE),
  preferredStart: z
    .object({
      option: z.nativeEnum(JOB_START_DATE),
      label: z.string().optional(),
      level: z.literal(48).optional(),
    })
    .optional(),
});

const transformJobRequest = <Data extends z.infer<typeof JobRequestBaseSchema>>(
  data: Data,
) => {
  const newPreferredStartIds = {
    Flexible: '018f809e-31fe-7ccd-b906-b4876df0c556',
    Urgent: '018f809e-5354-74c8-85e7-9aa6dead0de6',
    Within2Weeks: '018f809e-721e-71fb-b52d-3312dffdc86f',
    Within1Month: '018f809e-ad12-7811-ad8d-1d138f0e211d',
    InPlanning: '018f809e-8be9-7d90-837e-3a16b6d017f2',
    ReadyToBook: '018f809e-bdf9-4c32-a918-f52ccf194d78',
    Within1Week: null,
    WithinFewDays: null,
    Asap: null,
  };

  return {
    ...data,
    ...(data?.preferredStart &&
    newPreferredStartIds[data?.preferredStart.option]
      ? {
          preferredStart: {
            id: newPreferredStartIds[data?.preferredStart.option],
          },
        }
      : {}),
    type:
      data.type === JOB_TYPE.requestAQuote
        ? 'REQUEST_A_QUOTE'
        : 'DIRECT_MESSAGE',
  };
};

export const JobRequestSchema =
  JobRequestBaseSchema.transform(transformJobRequest);

export const JobRequestPublicSchema = JobRequestBaseSchema.extend({
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string(),
  communicationPreferences: z.array(
    z.object({ topic: z.string(), channel: z.record(z.boolean()) }),
  ),
}).transform(transformJobRequest);

export const ReasonSchema = z.object({
  id: z.string(),
  reason: z.string(),
  customReason: z.boolean(),
});

export type JobStatuses = keyof typeof JOB_STATUSES;

export const ReasonsSchema = z.array(ReasonSchema);

export type Reason = z.infer<typeof ReasonSchema>;
export type Reasons = z.infer<typeof ReasonsSchema>;

export type JobsResponse = z.infer<typeof JobsResponseSchema>;
export type JobResponse = z.infer<typeof JobResponseSchema>;
export type JobRequest = z.infer<typeof JobRequestSchema>;

export type Job = z.infer<typeof JobSchema> & { status: JobStatuses };
export type ContactedTrade = z.infer<typeof ContactedTradeSchema>;

export type JobRequestPublic = z.infer<typeof JobRequestPublicSchema>;
